import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStopwatch, faPlay, faPause, faStop, faClock, faTimes } from '@fortawesome/free-solid-svg-icons';

interface TimerModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (timerData: any) => void;
    timer?: any;
    mode: 'create' | 'edit' | 'view';
}

const TimerModal: React.FC<TimerModalProps> = ({ show, onHide, onSubmit, timer, mode }) => {
    const [formData, setFormData] = useState({
        title: timer?.title || '',
        description: timer?.description || '',
        timer_type: timer?.timer_type || 'custom',
        duration_minutes: timer?.duration_minutes || 25
    });

    const [timeLeft, setTimeLeft] = useState(timer?.duration_minutes * 60 || 25 * 60);
    const [isRunning, setIsRunning] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        
        if (isRunning && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft(prev => {
                    const newTime = prev - 1;
                    const totalTime = formData.duration_minutes * 60;
                    setProgress(((totalTime - newTime) / totalTime) * 100);
                    return newTime;
                });
            }, 1000);
        } else if (timeLeft === 0) {
            setIsRunning(false);
            // Timer completed
        }

        return () => clearInterval(interval);
    }, [isRunning, timeLeft, formData.duration_minutes]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
        onHide();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'duration_minutes' ? parseInt(value) || 0 : value
        }));
        
        if (name === 'duration_minutes') {
            const newDuration = parseInt(value) || 0;
            setTimeLeft(newDuration * 60);
            setProgress(0);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleStart = () => {
        setIsRunning(true);
    };

    const handlePause = () => {
        setIsRunning(false);
    };

    const handleStop = () => {
        setIsRunning(false);
        setTimeLeft(formData.duration_minutes * 60);
        setProgress(0);
    };

    const handleReset = () => {
        setTimeLeft(formData.duration_minutes * 60);
        setProgress(0);
        setIsRunning(false);
    };

    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header bg-success text-white">
                        <h5 className="modal-title">
                            <FontAwesomeIcon icon={faStopwatch} className="me-2" />
                            {mode === 'create' ? 'Create New Timer' : mode === 'edit' ? 'Edit Timer' : 'Timer'}
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onHide}></button>
                    </div>
                    <div className="modal-body">
                {mode === 'view' && timer ? (
                    // Timer View Mode
                    <div className="text-center">
                        <div className="timer-display mb-4">
                            <h1 className="display-1 text-primary">{formatTime(timeLeft)}</h1>
                            <h4 className="text-muted">{timer.title}</h4>
                        </div>
                        
                        <div className="progress mb-4" style={{ height: '20px' }}>
                            <div 
                                className={`progress-bar ${progress > 80 ? 'bg-danger' : progress > 60 ? 'bg-warning' : 'bg-success'}`}
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        
                        <div className="timer-controls mb-4">
                            {!isRunning ? (
                                <button 
                                    className="btn btn-success btn-lg me-2" 
                                    onClick={handleStart}
                                >
                                    <FontAwesomeIcon icon={faPlay} className="me-2" />
                                    Start
                                </button>
                            ) : (
                                <button 
                                    className="btn btn-warning btn-lg me-2" 
                                    onClick={handlePause}
                                >
                                    <FontAwesomeIcon icon={faPause} className="me-2" />
                                    Pause
                                </button>
                            )}
                            
                            <button 
                                className="btn btn-danger btn-lg me-2" 
                                onClick={handleStop}
                            >
                                <FontAwesomeIcon icon={faStop} className="me-2" />
                                Stop
                            </button>
                            
                            <button 
                                className="btn btn-secondary btn-lg" 
                                onClick={handleReset}
                            >
                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                Reset
                            </button>
                        </div>
                        
                        <div className="timer-info">
                            <p className="text-muted">
                                <strong>Type:</strong> {timer.timer_type} | 
                                <strong> Duration:</strong> {timer.duration_minutes} minutes
                            </p>
                            {timer.description && (
                                <p className="text-muted">{timer.description}</p>
                            )}
                        </div>
                    </div>
                ) : (
                    // Create/Edit Mode
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-8">
                                <div className="mb-3">
                                    <label className="form-label">Timer Title *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        required
                                        placeholder="Enter timer title"
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label className="form-label">Timer Type</label>
                                    <select
                                        className="form-select"
                                        name="timer_type"
                                        value={formData.timer_type}
                                        onChange={handleChange}
                                    >
                                        <option value="pomodoro">Pomodoro</option>
                                        <option value="break">Break</option>
                                        <option value="focus">Focus</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Description</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter timer description"
                            />
                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">Duration (minutes)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="duration_minutes"
                                        value={formData.duration_minutes}
                                        onChange={handleChange}
                                        min="1"
                                        max="1440"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex align-items-end h-100">
                                    <div className="timer-preview p-3 bg-light rounded">
                                        <small className="text-muted">Preview:</small>
                                        <div className="h4 mb-0 text-primary">
                                            {formatTime(formData.duration_minutes * 60)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Duration Buttons */}
                        <div className="mb-3">
                            <label className="form-label">Quick Duration</label>
                            <div className="d-flex gap-2 flex-wrap">
                                {[5, 15, 25, 30, 45, 60].map(duration => (
                                    <button
                                        key={duration}
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, duration_minutes: duration }));
                                            setTimeLeft(duration * 60);
                                            setProgress(0);
                                        }}
                                    >
                                        {duration} min
                                    </button>
                                ))}
                            </div>
                        </div>
                    </form>
                )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onHide}>
                            {mode === 'view' ? 'Close' : 'Cancel'}
                        </button>
                        {mode !== 'view' && (
                            <button type="button" className="btn btn-success" onClick={handleSubmit}>
                                {mode === 'create' ? 'Create Timer' : 'Update Timer'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimerModal;
